import { getStorageItem, setStorageItem, removeStorageItem } from '../utils/storage';
import type { SelectedRoutine } from '../types';

const DRAFT_KEY_PREFIX = 'gym-workout-draft';
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function draftKey(userId: string): string {
  return `${DRAFT_KEY_PREFIX}-${userId}`;
}

export interface WorkoutDraft {
  selectedRoutine: SelectedRoutine;
  confirmedSets: string[]; // serialized from Set<string>
  activeExerciseIndex: number;
  savedAt: number;
}

function serializeDraft(
  selectedRoutine: SelectedRoutine,
  confirmedSets: Set<string>,
  activeExerciseIndex: number
): string {
  const draft: WorkoutDraft = {
    selectedRoutine,
    confirmedSets: Array.from(confirmedSets),
    activeExerciseIndex,
    savedAt: Date.now(),
  };
  return JSON.stringify(draft);
}

export async function saveDraft(
  userId: string,
  selectedRoutine: SelectedRoutine,
  confirmedSets: Set<string>,
  activeExerciseIndex: number
): Promise<void> {
  const json = serializeDraft(selectedRoutine, confirmedSets, activeExerciseIndex);
  await setStorageItem(draftKey(userId), json);
}

/** Synchronous version for beforeunload (web only, uses localStorage directly) */
export function saveDraftSync(
  userId: string,
  selectedRoutine: SelectedRoutine,
  confirmedSets: Set<string>,
  activeExerciseIndex: number
): void {
  const json = serializeDraft(selectedRoutine, confirmedSets, activeExerciseIndex);
  localStorage.setItem(draftKey(userId), json);
}

export async function loadDraft(userId: string): Promise<WorkoutDraft | null> {
  const raw = await getStorageItem(draftKey(userId));
  if (!raw) return null;

  try {
    const draft: WorkoutDraft = JSON.parse(raw);
    if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      await clearDraft(userId);
      return null;
    }
    return draft;
  } catch {
    await clearDraft(userId);
    return null;
  }
}

export async function clearDraft(userId: string): Promise<void> {
  await removeStorageItem(draftKey(userId));
}
